import React, { useEffect, useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isRegistrationError, isRegistrationResponseExisting, registerUser, type RegistrationResponseCreated, type RegistrationSignupType } from '../../../../utils/requests/clientAuth/registration';
import { ControlledTextField } from '../../../../components/Forms';
import type { FormRegistrationError } from '../RegistrationForm';
import type { CapitalizedBusinessLine } from '../../../../components/SocialButtons';
import { OTHER_ERROR_CODE } from '../../../../utils/requests/clientAuth/types';
import { ErrorMessage } from '../ErrorMessage';
import { Heading, PanelLayout } from '../../../../components/OnboardingNavigation';
import { ActionBar } from '../../../../components/OnboardingNavigation/ActionBar';
export const FIRST_NAME_MAX = 30 as const;
const DEFAULT_NAME_FORM_TITLE = 'What’s your first name?';
const KIDS_NAME_FORM_TITLE = `${DEFAULT_NAME_FORM_TITLE} (We’ll ask your kid’s name next.)`;
export const nameSchema = z.object({
  name: z.string().min(1, {
    message: 'First name is required'
  }).max(FIRST_NAME_MAX, {
    message: `First name must be fewer than ${FIRST_NAME_MAX} characters`
  })
});
export type NameSchema = z.infer<typeof nameSchema>;
interface NameFormProps {
  onError: (error: FormRegistrationError) => void;
  onRegistration: (registration: RegistrationResponseCreated) => void;
  onSubmit: (name: NameSchema) => void;
  currentName?: string;
  email: string;
  businessLine: CapitalizedBusinessLine;
  zipCode?: string;
  registrationSignupType?: RegistrationSignupType;
  isWafEnabled?: boolean;
}
export const NameForm = ({
  onError,
  onRegistration,
  onSubmit,
  currentName = '',
  email,
  businessLine,
  zipCode,
  registrationSignupType,
  isWafEnabled
}: NameFormProps) => {
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: {
      errors
    }
  } = useForm<NameSchema>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      name: currentName
    }
  });
  const submit = handleSubmit(async name => {
    setLoading(true);
    onSubmit(name);
    try {
      const {
        data: clientAuthRegistrationResponse
      } = await registerUser({
        user: {
          first_name: name.name,
          business_line: businessLine,
          email,
          shipping_postcode: zipCode,
          signup_type: registrationSignupType
        },
        options: {
          name_params_optional: true
        }
      }, isWafEnabled);
      if (!isRegistrationResponseExisting(clientAuthRegistrationResponse)) {
        onRegistration(clientAuthRegistrationResponse);
      }
    } catch (error) {
      if (isRegistrationError(error)) {
        onError({
          type: 'email',
          data: error.response.data
        });
      }
      setLoading(false);
      setError('root', {
        message: OTHER_ERROR_CODE
      });
    }
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFocus('name');
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [setFocus]);
  return <PanelLayout maxWidth="424px" secondaryContent={<ActionBar isSkipShown={false} isLoading={loading} />} as="form" onSubmit={submit} aria-label="form" align="center-all">
      <Heading title={businessLine === 'Kids' ? KIDS_NAME_FORM_TITLE : DEFAULT_NAME_FORM_TITLE} />

      <ControlledTextField control={control as Control<NameSchema>} name="name" label="Name" autocomplete="off" maxLength={FIRST_NAME_MAX} placeholder="First Name" />

      {errors.root?.message === OTHER_ERROR_CODE && <ErrorMessage />}
    </PanelLayout>;
};